import React, { useState, useCallback } from 'react';
import ProcessRow from './ProcessRow';
import Button from '../common/Button';
import StatusMessage from '../common/StatusMessage';
import Loading from '../common/Loading';
import ComplexityImpactChart from '../charts/ComplexityImpactChart';
import { convertToCSV, downloadCSV, parseCSV, getCSVColumns } from '../../utils/csvParser';
import { sortAndRenumberProcesses } from '../../utils/scoreCalculator';
import { categories } from '../../utils/constants';
import { generateRatings } from '../../services/aiService';

function ProcessTable({ customerId, processes, setProcesses, contextData }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleProcessChange = useCallback((index, updatedProcess) => {
    setProcesses(prevProcesses => {
      const newProcesses = [...prevProcesses];
      newProcesses[index] = updatedProcess;
      return newProcesses;
    });
  }, [setProcesses]);

  const handleAddProcess = () => {
    const newProcess = {
      id: `PROC${processes.length + 1}`,
      name: '',
      summary: '',
      complexity: 'Medium',
      impact_productivity: 'None',
      impact_customer: 'None',
      impact_agent: 'None',
      impact_compliance: 'None',
      importance_productivity: 'None',
      importance_customer: 'None',
      importance_agent: 'None',
      importance_compliance: 'None'
    };
    setProcesses([...processes, newProcess]);
    setStatus({
      type: 'success',
      message: 'New process added! Fill in the details below.'
    });
  };


  const handleUploadCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const parsedProcesses = parseCSV(csvText);

        // Sort and renumber the processes
        const sortedProcesses = sortAndRenumberProcesses(parsedProcesses);
        setProcesses(sortedProcesses);

        // Auto-save to localStorage
        const storageKey = customerId ? `processes_${customerId}` : 'processes_default';
        localStorage.setItem(storageKey, JSON.stringify(sortedProcesses));

        setStatus({
          type: 'success',
          message: `✅ Loaded and saved ${sortedProcesses.length} processes! Ready for AI generation or manual editing.`
        });
      } catch (error) {
        setStatus({
          type: 'error',
          message: `Failed to parse CSV: ${error.message}`
        });
      }
    };
    reader.readAsText(file);
  };

  const handleGenerateAI = async () => {
    if (!contextData) {
      setStatus({ type: 'error', message: 'Please save context first to enable AI generation' });
      return;
    }

    if (!processes || processes.length === 0) {
      setStatus({ type: 'error', message: 'Please add or upload processes first' });
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      const result = await generateRatings(customerId || 'default', processes, contextData);
      setProcesses(result.processes);

      setStatus({
        type: 'success',
        message: `✅ AI ratings generated for ${result.processCount} processes!`
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Failed to generate AI ratings: ${error.response?.data?.error || error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResortProcesses = () => {
    if (processes.length === 0) {
      setStatus({ type: 'error', message: 'No processes to resort' });
      return;
    }

    const sortedProcesses = sortAndRenumberProcesses(processes);
    setProcesses(sortedProcesses);

    setStatus({
      type: 'success',
      message: `✅ Processes sorted by weighted score and renumbered!`
    });
  };

  const handleSaveProcesses = () => {
    if (processes.length === 0) {
      setStatus({ type: 'error', message: 'No processes to save' });
      return;
    }

    // Sort and renumber before saving
    const sortedProcesses = sortAndRenumberProcesses(processes);
    setProcesses(sortedProcesses);

    // Save to localStorage (use customerId if available, otherwise use 'default')
    const storageKey = customerId ? `processes_${customerId}` : 'processes_default';
    localStorage.setItem(storageKey, JSON.stringify(sortedProcesses));

    setStatus({
      type: 'success',
      message: `✅ Saved ${sortedProcesses.length} processes! Ready to generate report.`
    });
  };

  const handleExportCSV = () => {
    if (processes.length === 0) {
      setStatus({ type: 'error', message: 'No processes to export' });
      return;
    }

    // Sort and renumber before export
    const sortedProcesses = sortAndRenumberProcesses(processes);

    const columns = getCSVColumns('final');
    const csvContent = convertToCSV(sortedProcesses, columns);

    // Use customerId for consistent naming with context files
    const filename = customerId ? `${customerId}-processes.csv` : 'processes.csv';
    downloadCSV(csvContent, filename);

    setStatus({
      type: 'success',
      message: `✅ Exported ${sortedProcesses.length} processes to CSV!`
    });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Process Assessment</h2>
      <p className="text-gray-600 text-sm mb-6">Upload a CSV or manually add processes, then rate them across Impact, Importance, and Complexity. Use AI to generate ratings automatically.</p>

      {status && (
        <StatusMessage
          type={status.type}
          message={status.message}
          onClose={() => setStatus(null)}
        />
      )}

      <div className="flex gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload CSV
          </label>
          <input
            type="file"
            accept=".csv"
            onChange={handleUploadCSV}
            className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add Process Manually
          </label>
          <Button
            variant="secondary"
            onClick={handleAddProcess}
            className="py-2 px-4"
          >
            + Add Process
          </Button>
        </div>
      </div>

      {loading && <Loading message="Generating AI ratings... This may take 1-5 minutes." />}

      {!loading && processes.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-200">
                <tr>
                  <th className="border border-gray-300 px-2 py-2 text-xs">ID</th>
                  <th className="border border-gray-300 px-2 py-2 text-xs">Process Name</th>
                  <th className="border border-gray-300 px-2 py-2 text-xs">Summary</th>
                  <th className="border border-gray-300 px-2 py-2 text-xs">Complexity</th>
                  <th className="border border-gray-300 px-2 py-2 text-xs">Score</th>
                  <th className="border border-gray-300 px-2 py-2 text-xs">Category</th>

                  {/* Category headers */}
                  {categories.map((cat) => (
                    <th key={cat.key} className="border border-gray-300 px-2 py-2 text-xs">
                      {cat.label}
                    </th>
                  ))}

                  <th className="border border-gray-300 px-2 py-2 text-xs bg-green-100">
                    Weighted Score
                  </th>
                  <th className="border border-gray-300 px-2 py-2 text-xs bg-yellow-100">
                    Weighted Impact
                  </th>
                </tr>
              </thead>
              <tbody>
                {processes.map((process, index) => (
                  <ProcessRow
                    key={process.id || index}
                    process={process}
                    onProcessChange={(updated) => handleProcessChange(index, updated)}
                    contextData={contextData}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button
              variant="primary"
              onClick={handleGenerateAI}
              disabled={loading || processes.length === 0}
            >
              {loading ? 'Generating...' : 'Generate AI Ratings'}
            </Button>

            <Button
              variant="secondary"
              onClick={handleResortProcesses}
              disabled={processes.length === 0}
            >
              Resort Processes
            </Button>

            <Button
              variant="success"
              onClick={handleSaveProcesses}
              disabled={processes.length === 0}
            >
              Save Process List
            </Button>

            <Button
              variant="secondary"
              onClick={handleExportCSV}
              disabled={processes.length === 0}
            >
              Export as CSV
            </Button>
          </div>

          {/* Complexity-Impact Chart */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Process Prioritization Chart</h3>
            <ComplexityImpactChart processes={processes} />
          </div>
        </>
      )}

      {!loading && processes.length === 0 && (
        <p className="text-gray-600 text-center py-8">
          No processes loaded. Upload a CSV or add processes manually.
        </p>
      )}
    </div>
  );
}

export default ProcessTable;
